// Pricing tiers — source of truth for plan limits and Stripe product mapping.
// Locked at MVP per kickoff brainstorm. Update in lockstep with Stripe products
// AND with the gating logic in apps/dashboard.

// PRICING REPACKAGE — 2026-08-08 (operator-approved). Read this before touching PLANS.
//
// Display name and internal plan KEY are deliberately decoupled — do not "fix"
// this by renaming keys, it would trigger a full data/DB/gating migration for
// zero benefit:
//   - Displayed "Starter" ($99/mo) === internal key `pro`. This is the tier
//     that used to be sold as "Pro" at $49; only the label and price changed.
//   - The internal key `business` displays as "Business" ($249/mo, was $99).
//   - The internal key `growth` displays as "Growth" ($499/mo — raised from
//     $399 on 2026-08-10 when multi-location shipped, per the locked pricing
//     decision; existing subscribers keep their old price).
//   - The internal key `starter` (the old $19/mo tier) is RETIRED — no Stripe
//     prices exist for it anymore, no signup path offers it, and it must never
//     appear on any customer-facing pricing surface. The key is kept ONLY so
//     existing types/records (incl. any historical row still carrying
//     `tier = 'starter'`) continue to typecheck. See the field comment below.
// Stripe: live catalog migrated for the 2026-08-08 repricing (product
// "pageboss Pro" renamed to "pageboss Starter"; prices tagged `metadata.plan`;
// old prices archived; legacy $19 Starter product archived). Any LATER amount
// change here (e.g. the 2026-08-10 Growth $399→$499 raise) is NOT live on
// Stripe until `node scripts/stripe-reprice.mjs --execute` +
// `npm run secrets:push` run — always verify the three sources (this file ·
// vault STRIPE_PRICE_* · live Stripe) agree before deploying a price change.
export const PLANS = {
  // LEGACY / RETIRED 2026-08-08 — do not sell. No Stripe prices exist for this
  // key anymore and no signup surface offers it. Kept valid only so old code
  // paths and any historical `tier = 'starter'` row still typecheck; never
  // render this on a pricing page or upgrade teaser (public pricing surfaces
  // — marketing TIERS, dashboard PLAN_ORDER — must enumerate only
  // pro/business/growth). `label` stays a plain, customer-safe string (not an
  // internal note) because a grandfathered customer's billing page reads it
  // directly (`PLANS[current.plan].label`) — never leak "retired"/"legacy"
  // copy into a paying customer's UI.
  starter: {
    label: 'Starter',
    monthlyPriceCents: 1900,
    annualPriceCents: 19000,
    pageLimit: 5,
    customDomain: false,
    aiGenerationsMonthly: 50,
    multiLocation: false,
    locationsIncluded: 0,
    freeDomain: false,
    freeDomainsIncluded: 0,
    freeMailboxesIncluded: 0,
    whiteLabel: false,
    trackingMonthly: 3000,
  },
  // Displayed publicly as "Starter" (repriced 2026-08-08; formerly displayed
  // as "Pro" at $49/mo). Internal key stays `pro` — see block comment above.
  pro: {
    label: 'Starter',
    monthlyPriceCents: 9900,
    annualPriceCents: 99000,
    pageLimit: 10,
    customDomain: true,
    aiGenerationsMonthly: 100,
    multiLocation: false,
    locationsIncluded: 0,
    // Repackaging 2026-08-08: 1 free CF-registered domain now included at
    // entry (was 0 — Pro/Starter previously had to bring their own). Both
    // freeDomain and freeDomainsIncluded must flip together — the actual
    // gate (apps/dashboard/src/lib/gating-rules.ts's tierAllowsFreeDomain)
    // reads the freeDomain BOOLEAN, not this count; freeDomainsIncluded alone
    // is display/count metadata and does not, by itself, unlock anything.
    freeDomain: true,
    freeDomainsIncluded: 1,
    freeMailboxesIncluded: 1,
    whiteLabel: false,
    trackingMonthly: 12000,
  },
  business: {
    label: 'Business',
    monthlyPriceCents: 24900,
    annualPriceCents: 249000,
    pageLimit: 25,
    customDomain: true,
    aiGenerationsMonthly: 250,
    multiLocation: false,
    locationsIncluded: 0,
    freeDomain: true,
    freeDomainsIncluded: 1,
    freeMailboxesIncluded: 5,
    whiteLabel: false,
    trackingMonthly: 25000,
  },
  growth: {
    label: 'Growth',
    monthlyPriceCents: 49900,
    annualPriceCents: 499000,
    pageLimit: 100,
    customDomain: true,
    aiGenerationsMonthly: 1000,
    multiLocation: true,
    locationsIncluded: 3,
    // freeDomainsIncluded corrected 2026-08-08 (was 3): the money-path gate
    // (apps/dashboard/src/routes/api/domains.ts, the domains_org_free_idx
    // partial unique index in packages/db/src/schema.ts) only ever allows ONE
    // non-deleted free-tier domain per org, on every tier including Growth —
    // this field is display/count metadata only (tierAllowsFreeDomain reads
    // the freeDomain BOOLEAN, not this count) but it still must match reality.
    // Delivering 3 needs a slot-indexed unique constraint + a race-safe
    // multi-slot claim flow — real money-path work, not a copy fix. Restore
    // to 3 only alongside that migration.
    freeDomain: true,
    freeDomainsIncluded: 1,
    freeMailboxesIncluded: 10,
    whiteLabel: true,
    trackingMonthly: 60000,
  },
} as const;

export type PlanKey = keyof typeof PLANS;

// Backend-only tracking metering. NEVER surfaced in customer UI/pricing (locked #11).
// Single calibration file — change here, nowhere else. Calibrate the credit
// coefficients + monthly pools week-1 from real DataForSEO invoices.
export const TRACKING = {
  credits: { geoPinPerKeyword: 1, aiCheck: 15, aiSamples: 3 }, // operator-only coefficients
  // Planning pools, raised 2026-08-02 (D2). Our data cost is a rounding error
  // next to the plan price, and the category's number-one complaint is credits
  // burning out — so generosity is the weapon. Margin re-derived in
  // docs/plans/2026-06-15-tracking-cost-analysis.md (2026-08-02 section):
  // every tier stays positive at its absolute floor.
  monthly: { free: 0, starter: 3000, pro: 12000, business: 25000, growth: 60000 },
  overagePack: { creditsPerPack: 1000, priceCents: 900 }, // $9/pack, mirrors AI_OVERAGE_PACK; size is a planning value
  geoTiers: ['starter', 'pro', 'business', 'growth'] as const, // geo-grid gate — all paid tiers (repackaging 2026-06-15)
  aiTiers: ['starter', 'pro', 'business', 'growth'] as const, // AI visibility gate — all paid tiers, engine-count-gated
  // AI engine coverage by tier (Phase 11, repackaged 2026-06-15). Engine COUNT is
  // the tier lever: Starter 1 → Pro 2 → Business 3 → Growth all. Order = value
  // (Google AI → ChatGPT → Gemini → +Claude,Perplexity). Only engines with a live
  // adapter are listed (caps AI COGS + keeps the shown allowance honest). The
  // quota leaf (ai-visibility-quota.ts) inlines these; a test asserts lockstep.
  aiEngines: {
    starter: ['google-aio'],
    pro: ['google-aio', 'openai'],
    business: ['google-aio', 'openai', 'gemini'],
    growth: ['google-aio', 'openai', 'gemini', 'anthropic', 'perplexity'],
  },
  // Grid sizes the owner picks from (D1, locked 2026-08-02). SQUARE lattices —
  // the pin counts (25/49/81/169) are what the cost model and the coverage copy
  // both quote, and a circle mask would silently drop 7x7 to 29. Odd sizes only
  // so the business sits on the exact centre pin. The water mask still applies
  // and still refunds. This is the canonical geometry; the customer-facing copy
  // lives in geo-grid-options.ts with a lockstep drift test.
  gridOptions: {
    '5x5': { gridSize: 5, shape: 'square', pointDistanceKm: 1.2 },
    '7x7': { gridSize: 7, shape: 'square', pointDistanceKm: 1.2 }, // default
    '9x9': { gridSize: 9, shape: 'square', pointDistanceKm: 2 },
    '13x13': { gridSize: 13, shape: 'square', pointDistanceKm: 3 },
  },
  // LEGACY. Superseded by gridOptions (D1). Kept because historical geo_scans
  // rows carry these keys in `profile`; nothing new writes them.
  scanProfiles: {
    storefront: { gridSize: 13, shape: 'circle', pointDistanceKm: 1.2 }, // default
    wide: { gridSize: 9, shape: 'circle', pointDistanceKm: 5 }, // SAB / service-area
  },
  // Cron drainer calibration (Phase 10 refresh). One-file tuning for the
  // geo-scan drain cron (`*/5 * * * *` → geo-scan-drain), now two-phase:
  //   maxSubmitPerTick   — pins SUBMITTED per scan per tick. One task_post
  //                        carries up to maxTasksPerPost of them, so this is
  //                        cheap in subrequests: 200 pins = 2 HTTP calls.
  //   maxTasksPerPost    — DataForSEO's hard cap on one task_post body.
  //   maxCollectPerTick  — task_get calls per scan per tick (one per pin; free,
  //                        but each is a subrequest).
  //   maxScansPerTick    — distinct queued/running scans claimed each tick.
  //   cacheTtlS          — KV cache TTL passed to the adapter (24h; rank data is
  //                        recomputed daily, so a 1-day cache is the margin).
  //   partialMinAnsweredFraction — D3 (2026-08-06 scan-failure fix): a scan
  //                        that exhausts its attempt budget still COMPLETES,
  //                        billing only answered pins, when at least this
  //                        fraction of its non-water pins have an answer;
  //                        below it the scan fails with a full refund. At 0.8
  //                        the 2026-08-03 casualties split honestly: 79/81 and
  //                        77/81 deliver their maps, 57/81 refunds.
  drain: {
    maxSubmitPerTick: 200,
    maxTasksPerPost: 100,
    maxCollectPerTick: 60,
    maxScansPerTick: 3,
    cacheTtlS: 86400,
    partialMinAnsweredFraction: 0.8,
  },
  /** Which DataForSEO queue an OWNER-INITIATED check uses.
   *
   *  This is a cost/latency policy, not a detail — priority is 2x the price
   *  per pin ($0.0012 vs $0.0006, measured), bought so someone watching the
   *  screen gets an answer fast. Our own drain runs every 5 minutes, which
   *  caps how much of that speed ever reaches them, so the premium is only
   *  worth paying if the standard queue is genuinely slower than that cadence.
   *  Kept here, next to the rest of the tracking economics, so the decision is
   *  a config change with its reasoning attached rather than a literal buried
   *  in a route. The weekly cron always uses 'standard' — nobody is waiting.
   *
   *  MEASURED END TO END 2026-08-03, same site, same 5x5 grid, fresh keywords
   *  so nothing came from cache:
   *      priority  7.7 min   ($0.0012/pin)
   *      standard 11.2 min   ($0.0006/pin)
   *  Both found 25/25. The documented "up to 45 min" for standard did not
   *  happen. The gap is 3.5 minutes — about ONE extra five-minute cron tick,
   *  because our own cadence dominates either way (DataForSEO itself answers a
   *  priority task in ~12s and we then sit on it until the next tick).
   *
   *  So: standard. Paying double for one tick is not a trade worth making, and
   *  the honest way to buy back that time is a tighter cron, which costs
   *  nothing at the provider and helps both modes.
   *
   *  Caveat, stated because it is one sample at one time of day: DataForSEO's
   *  standard queue varies with THEIR load. If the wait starts drifting toward
   *  the documented 45 minutes, this is the one line to flip back — and the
   *  "about ten minutes" copy on the rank screen has to move with it. */
  interactiveQueueMode: 'standard' as 'priority' | 'standard',
  /** Maps zoom used for EVERY pin of a scan. Fixed on purpose (design §A3):
   *  zoom changes a pin's effective radius, so a drifting zoom makes pins
   *  incomparable and week-over-week trend meaningless. */
  defaultScanZoom: 14,
  // AI visibility drainer calibration (Phase 11b, cron `*/15`). Fewer calls per
  // tick than geo — each is a full LLM/search round-trip (slower, pricier) and a
  // scan fans out prompts×engines×3 samples. cacheTtlS is 24h (AI answers drift
  // daily, so a 1-day cache is the margin).
  aiDrain: { maxCallsPerTick: 20, maxScansPerTick: 2, cacheTtlS: 86400 },
} as const;

export type ScanProfileKey = keyof typeof TRACKING.scanProfiles;

/** What the CUSTOMER buys, in units they can repeat back to you.
 *
 *  Tracking is metered on two independent layers, and confusing them is the
 *  mistake this block exists to prevent:
 *
 *    1. THIS table is the product. An owner buys "3 search terms, 3
 *       competitors, 5 AI questions, a weekly report". Enforcement happens at
 *       REGISTRATION time -- you may not save a 4th search term on a plan that
 *       includes 3. Nothing here is a credit and nothing here is derived from
 *       one; these numbers appear verbatim on the pricing page.
 *
 *    2. TRACKING.credits / TRACKING.monthly stay exactly as they are, but
 *       ONLY as the internal abuse ceiling behind the reserve/settle/refund
 *       ledger. They are never sold, never shown, and never quoted to a
 *       customer (locked decision #11). A future session that finds both
 *       layers and "simplifies" one away breaks either the abuse guard or the
 *       promise on the pricing page -- keep both.
 *
 *  Why grid size and cadence live HERE rather than being owner choices: a
 *  promise of "3 search terms" is only cost-bounded if the geometry and the
 *  frequency behind each term are fixed. Bigger grid then becomes a reason to
 *  upgrade, which is a far easier thing to sell than "more credits".
 *
 *  Tier keys are INTERNAL. The tier sold as "Starter" is `pro`; the `starter`
 *  key is the retired $19 plan and gets zeros (see PLANS above for the full
 *  trap). Grid values are string-duplicated from geo-grid-options.ts rather
 *  than imported, matching how TRACKING.gridOptions already does it -- both
 *  files are import-free leaves. A lockstep test guards the drift.
 *
 *  Design: docs/plans/2026-08-08-tracking-plain-units-design.md */
export const TRACKING_PLAN = {
  free: { searchTerms: 0, competitors: 0, aiQuestions: 0, gridOption: '7x7', cadence: 'weekly' },
  starter: { searchTerms: 0, competitors: 0, aiQuestions: 0, gridOption: '7x7', cadence: 'weekly' },
  pro: { searchTerms: 3, competitors: 3, aiQuestions: 5, gridOption: '7x7', cadence: 'weekly' },
  business: {
    searchTerms: 10,
    competitors: 10,
    aiQuestions: 20,
    gridOption: '9x9',
    cadence: 'weekly',
  },
  growth: {
    searchTerms: 25,
    competitors: 25,
    aiQuestions: 50,
    gridOption: '13x13',
    cadence: 'weekly',
  },
} as const;

// AI overage: $9 buys 100 additional generations. Stacks with monthly quota.
export const AI_OVERAGE_PACK = {
  generations: 100,
  priceCents: 900,
} as const;

// Mailbox add-on (Phase 6c) — recurring per-mailbox upsell.
export const MAILBOX_ADDON = { label: 'Mailbox', priceCents: 490 } as const;

// Search-term capacity add-on (plain-units Task 6, operator-locked 2026-08-08):
// +5 tracked search terms for $19/mo, quantity-based like MAILBOX_ADDON so an
// owner may hold several packs. SEARCH TERMS ONLY — never AI questions (an AI
// question is ~15x the unit cost of a map pin and needs its own price, not a
// share of this one). With manual scans gone (Task 4), this is the ONLY path
// to REGISTERING more terms than the plan includes — a launch requirement, not
// a follow-up. (The $9 tracking-overage pack still exists separately: it tops
// up the internal monthly allowance the drains check, not how many terms may
// be registered.) Price is locked; if a future cost measurement moves the
// floor, surface it rather than silently repricing.
// `maxPacks` is a DRAIN ceiling, not a sales preference: the Monday geo drain
// runs one schedule inside a single Worker invocation, sized against the
// drains' 900-query operating budget (D1's hard 1,000 per invocation minus a
// 100-query reserve), and pin-insert cost scales with search terms x grid
// pins. At the Growth grid (13x13 = 169 pins), 25 base + 3x5 pack terms = 40
// terms ≈ 861 queries — inside the 900; a 4th pack (45 terms ≈ 967) would
// break it. (Corrected 2026-09-24: this note used to say a 4th pack's weekly
// report could NEVER finish. It would — ≈ 967 plus the tick's ~3 measured
// overhead stays under D1's 1,000 when it runs alone — but it would consume
// the reserve and break the operating budget; the cap is unchanged.) The
// arithmetic lives in apps/jobs schedule-budget-core.ts, and a lockstep test
// there fails if either side moves without the other. Raising maxPacks
// requires re-deriving that ceiling first.
//
// A SECOND ceiling used to bind harder and was fixed rather than capped
// around (2026-08-09): the scan drain's attempt budget was a TOTAL-tick bound,
// silently capping every scan at 900 answered pins — under even Growth's BASE
// 25 x 169 = 4,225. It now bounds consecutive STALLED ticks only
// (scanMayContinue in apps/jobs geo-drain-core.ts), so big scans grind to
// completion: a max-packed Growth scan is ~6,760 pins ≈ 9.4h of Monday ticks —
// same accepted wall-clock class as the AI drain's ~9.5h Growth scan.
//
// `allowancePerPack` closes the other half of the pack (multi-location fix
// round 2, 2026-08-09): a pack RAISES how many terms may be registered, so it
// must also raise the org's internal monthly tracking allowance those extra
// terms will spend — otherwise an owner buys 5 more terms and their weekly
// report starts skipping on `allowance_exhausted`. DERIVED, 5-MONDAY-SAFE:
// 5 terms x 169 pins (the Growth 13x13 grid, the biggest sold) x 5 Mondays
// = 4,225, rounded up to 4,250 for headroom. FIVE Mondays, not 4.33 weeks:
// a calendar month can contain five of them, and a month that under-covers
// its own scans is a month the owner paid for a report they don't get.
// Backend-only, never surfaced (locked decision #11) — the customer sees
// "+5 search terms", never a credit figure.
export const SEARCH_TERM_ADDON = {
  label: 'Search terms',
  termsPerPack: 5,
  priceCents: 1900,
  maxPacks: 3,
  allowancePerPack: 4250, // backend-only, never surfaced (#11)
} as const;

// Extra-location add-on (multi-location phase,
// docs/plans/2026-08-09-multi-location-design.md, operator-locked
// 2026-08-09): $99/mo buys ONE extra location-site above Growth's included 3
// (PLANS.growth.locationsIncluded), carrying its own smaller tracking
// capacity -- 10 search terms, 10 competitors -- NOT a share of the tier's
// 25/25. THE LIVE READER of these two numbers is
// apps/dashboard/src/lib/gating-rules.ts's locationTrackingCapacity (every
// enforcement path -- geo schedule PUT, competitor cap, AI question gate --
// goes through it); packages/shared/src/location-groups.ts's roleCapacity is
// the dependency-free REFERENCE implementation of the same rule, kept for the
// leaf test, not wired into a request path. An earlier version of this
// comment named roleCapacity as "the reader", which sent sessions editing a
// module no route calls.
// AI questions are NOT per-extra -- the org's shared 50 may be spent on any
// location's schedule (a group-aware capacity check), so there is
// deliberately no extraAiQuestions field here.
// `maxExtra` is a DRAIN ceiling, not a sales preference -- same class as
// SEARCH_TERM_ADDON.maxPacks above. Growth's included 3 + up to 3 extra = 6
// location-sites/org, each running its own weekly geo schedule at the tier's
// 13x13 grid (169 pins). Priced by role that is [544, 544, 544, 227, 227,
// 227] queries without search-term packs (with packs each included site's
// schedule rises to 650-861 and runs via the progress guarantee, one per
// tick). Admission is PER TICK against the geo drain's ordinary 600
// ceiling -- NOT a single 6x900 pool, which was the wrong model the lockstep
// test used to assert -- so packing those six schedules greedily takes FIVE
// of the six hourly Monday ticks (09:00-14:00 UTC). Said honestly: ONE maxed
// group consumes roughly five of the platform's six Monday geo ticks either
// way, leaving one tick of ordinary geo headroom for every other org that
// Monday (without packs the AI drain keeps its 300 floor in every tick; in a
// tick where a packed ~861 schedule fired it has ~39 left, under one Growth
// AI schedule's ~124). That is the real cost of raising
// maxExtra, and it is why raising it requires re-deriving the ceiling first.
// This leaf cannot import apps/jobs, so the arithmetic is inlined with source
// comments in the lockstep test at the bottom of location-groups.test.ts.
export const LOCATION_ADDON = {
  label: 'Location',
  priceCents: 9900,
  maxExtra: 3,
  extraSearchTerms: 10,
  extraCompetitors: 10,
  // DERIVED, 5-MONDAY-SAFE (fix round 2, 2026-08-09): 10 terms x 169 pins
  // (the Growth 13x13 grid) x 5 Mondays = 8,450, rounded up to 8,500 for
  // headroom. The old 7,500 was derived from 4.33 weeks and UNDER-COVERED
  // every calendar month that happens to contain five Mondays -- the owner
  // paid $99 and the fifth week's report skipped on `allowance_exhausted`.
  // Backend-only, never surfaced (locked decision #11).
  allowancePerExtra: 8500,
} as const;

// Survey module (survey.pageboss.com) — a +$10/mo add-on that REQUIRES a paid
// base plan (Free = teaser only → must upgrade to activate). Mirrors the
// mailbox add-on plumbing. A single-survey teaser is available on every tier
// (incl. free) with no add-on; full limits scale to the org's paid tier once
// the add-on is active. See docs/plans/2026-07-17-survey-module.
// `overagePack` = the response-pack design's (2026-08-10) $9/1,000-response
// escrow-release pack (`docs/superpowers/specs/2026-08-10-survey-response-pack-design.md`).
// THE SINGLE SOURCE for that price and pack size: the billing route's Stripe
// charge, the meter payload, the billing-page button copy, and gating.ts's
// monthlyResponseAllowance all read it from here. (A flat `SURVEY_OVERAGE_PACK
// = { responses, priceCents }` used to sit beside this with the same numbers;
// it was deleted on 2026-08-10 when the purchase route landed — two constants
// for one price is how a repricing ships half-applied. escrow.ts's capacityFor
// inlines the 1,000 because it is an import-free leaf; a lockstep test in
// escrow.test.ts pins that literal to `overagePack.packSize`.)
export const SURVEY_ADDON = {
  label: 'Survey',
  priceCents: 1000,
  overagePack: { priceCents: 900, packSize: 1000 },
} as const;

// One-time charge to remove the "Powered by pageboss" badge from the public
// runner (also doubles as lead-gen for the future standalone product).
export const SURVEY_WHITE_LABEL = { priceCents: 9900 } as const;

// Lifetime invite ceiling for a SINGLE survey (deferred close-out Task 2,
// 2026-08-10). This is an ABUSE CEILING, not a plan feature — SURVEY_LIMITS
// above governs what a tier is SOLD (published surveys, monthly responses,
// email-invite access); this constant is a backend-only guard against one
// survey's invite table growing without bound (a POST is already capped at
// 200 rows and rate-limited to 6/min, but nothing previously stopped repeated
// calls from accumulating past that per-call cap). Mirrors the TRACKING
// credit ceilings' role (locked decision #11): never surfaced as a number on
// any pricing/plan surface, never quoted to a customer — the invite-creation
// route enforces it and returns a plain "this survey has reached its invite
// limit" message, not this figure.
export const SURVEY_INVITE_MAX_PER_SURVEY = 10_000;

// Teaser (any tier incl. free, no add-on): a single-survey taste to drive
// activation. Full builder/runner UX, just capped hard.
export const SURVEY_TEASER = {
  publishedSurveys: 1,
  monthlyResponses: 25,
  emailInvites: false,
} as const;

// Add-on active: limits by the org's paid tier. `publishedSurveys: null` =
// unlimited. Email invites (the "how we did" channel) are Business+.
export const SURVEY_LIMITS = {
  starter: { publishedSurveys: 3, monthlyResponses: 500, emailInvites: false },
  pro: { publishedSurveys: 10, monthlyResponses: 2000, emailInvites: false },
  business: { publishedSurveys: 25, monthlyResponses: 5000, emailInvites: true },
  growth: { publishedSurveys: null, monthlyResponses: 15000, emailInvites: true },
} as const satisfies Record<
  PlanKey,
  { publishedSurveys: number | null; monthlyResponses: number; emailInvites: boolean }
>;

export type SurveyTierLimits = {
  publishedSurveys: number | null;
  monthlyResponses: number;
  emailInvites: boolean;
};

// Demo lifecycle (kickoff Q4).
export const DEMO_TTL_DAYS = 90;
export const DEMO_REENGAGE_DAYS = [60, 85] as const;

// Refund window (kickoff Q17).
export const MONEY_BACK_GUARANTEE_DAYS = 30;

// WP6 — the ONE support address. Every surface (help slide-over mailto,
// email footers, marketing contact copy) must import this; the hand-kept
// copies in apps/marketing content.ts / Faq.tsx and dashboard
// unsubscribe.ts/billing.tsx are swept by WP4/WP5 (inventory 04 §3).
export const SUPPORT_EMAIL = 'support@pageboss.com';

// WP6 — concierge (done-for-you) service prices, as marketed since the
// Phase-2 marketing pages. NOT a Stripe SKU and NOT purchasable at checkout
// (verified 2026-08-16: zero `concierge` hits in apps/dashboard) — the only
// real path is email; it is invoiced manually. Single-sources the figures the
// FAQ interpolates; adding a checkout flow is a separate, unplanned decision.
export const CONCIERGE = {
  polishPriceCents: 9900, // "$99 one-time — starting site polished and launched with you"
  fullPriceCents: 49900,  // "$499 — full white-glove setup"
} as const;

// Dunning policy (kickoff Q18).
export const DUNNING_STAGES = [
  { day: 1, action: 'email_and_dashboard_banner' },
  { day: 3, action: 'second_email' },
  { day: 7, action: 'third_email_and_offline_warning' },
  { day: 10, action: 'site_serves_hold_page' },
  { day: 30, action: 'full_deactivate' },
] as const;

// Block library — the 15 types covering 80%+ of SMB site use cases.
// (12 from kickoff Q6 + `pricing` in 3B-3.5g + `credentials` in 3B-3.5h +
// `locations` in the multi-location phase Task 9, Scenario B.)
export const BLOCK_TYPES = [
  'hero',
  'services',
  'pricing',
  'gallery',
  'contact',
  'about',
  'testimonials',
  'cta',
  'heading',
  'text',
  'faq',
  'team',
  'credentials',
  'locations',
  'footer',
] as const;

export type BlockType = (typeof BLOCK_TYPES)[number];

/** Free /audit: default daily cap on Google Places lookups (operator,
 *  2026-09-03). The dashboard reads PUBLIC_AUDIT_PLACES_DAILY_CAP and falls
 *  back to this; the operator console and the morning mail display it. */
export const PUBLIC_AUDIT_PLACES_DAILY_CAP_DEFAULT = 100;
